import React, { useMemo, useState } from "react";
import {
  Image as ImageIcon,
  Play,
  TrendingUp,
  Zap,
  Flame,
} from "lucide-react";
import { STATUSES } from "../services/theWebData";

/**
 * WebGridView — masonry-ish responsive grid of ad creatives.
 * Sortable by ROAS / spend / hook rate / recency, with a status filter
 * inherited from the parent toolbar.
 *
 * Click → modal. Same click contract as the graph view.
 */
const SORTS = [
  { key: "roas",   label: "ROAS",      get: (n) => n.roas || 0 },
  { key: "spend",  label: "Spend",     get: (n) => n.spend || 0 },
  { key: "hook",   label: "Hook rate", get: (n) => n.hookRate || 0 },
  { key: "ctr",    label: "CTR",       get: (n) => n.ctr || 0 },
  { key: "label",  label: "Name",      get: (n) => (n.label || "").toLowerCase() },
];

// "Active" includes scaling — a scaling creative is an active one doing well.
const STATUS_FILTERS = [
  { key: "active", label: "Active", match: (s) => s === "active" || s === "scaling" },
  { key: "paused", label: "Paused", match: (s) => s === "paused" },
  { key: "killed", label: "Killed", match: (s) => s === "killed" },
  { key: "all",    label: "All",    match: () => true },
];

export default function WebGridView({ nodes, onSelect, selectedId, dimmedIds }) {
  const [sortKey, setSortKey] = useState("roas");
  const [direction, setDirection] = useState("desc");
  const [statusKey, setStatusKey] = useState("active");

  const creatives = useMemo(
    () => nodes.filter((n) => n.type === "concept" || n.type === "variation"),
    [nodes],
  );

  const counts = useMemo(() => {
    const c = {};
    STATUS_FILTERS.forEach((f) => {
      c[f.key] = creatives.filter((n) => f.match(n.status || "active")).length;
    });
    return c;
  }, [creatives]);

  const items = useMemo(() => {
    const filter = STATUS_FILTERS.find((f) => f.key === statusKey) || STATUS_FILTERS[0];
    const sortable = creatives.filter((n) => filter.match(n.status || "active"));
    const sort = SORTS.find((s) => s.key === sortKey) || SORTS[0];
    const sorted = [...sortable].sort((a, b) => {
      const av = sort.get(a);
      const bv = sort.get(b);
      if (typeof av === "string") return av.localeCompare(bv);
      return (bv || 0) - (av || 0);
    });
    return direction === "desc" ? sorted : sorted.reverse();
  }, [creatives, statusKey, sortKey, direction]);

  return (
    <div className="web-grid-wrap">
      <div className="web-grid-toolbar">
        <span className="web-grid-toolbar__label">Show</span>
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            className="web-chip"
            data-active={statusKey === f.key}
            onClick={() => setStatusKey(f.key)}
          >
            {f.label}
            <span style={{ opacity: 0.5, marginLeft: 4 }}>{counts[f.key]}</span>
          </button>
        ))}

        <span className="web-grid-toolbar__label" style={{ marginLeft: 16 }}>Sort by</span>
        {SORTS.map((s) => (
          <button
            key={s.key}
            className="web-chip"
            data-active={sortKey === s.key}
            onClick={() => {
              if (sortKey === s.key) {
                setDirection((d) => (d === "desc" ? "asc" : "desc"));
              } else {
                setSortKey(s.key);
                setDirection("desc");
              }
            }}
          >
            {s.label}
            {sortKey === s.key ? <span style={{ opacity: 0.5 }}>{direction === "desc" ? " ↓" : " ↑"}</span> : null}
          </button>
        ))}
        <span style={{ marginLeft: "auto", color: "var(--w-muted)", fontSize: 12 }}>
          {items.length} {items.length === 1 ? "creative" : "creatives"}
        </span>
      </div>

      <div className="web-grid">
        {items.map((n) => (
          <GridTile
            key={n.id}
            node={n}
            selected={n.id === selectedId}
            dimmed={dimmedIds?.has?.(n.id)}
            onClick={() => onSelect(n.id)}
          />
        ))}
        {items.length === 0 ? (
          <div className="web-grid__empty">
            No creatives match the current filters. Try "All" in the Show filter
            above, or loosen the filter chips in the toolbar.
          </div>
        ) : null}
      </div>
    </div>
  );
}

const GridTile = ({ node, selected, dimmed, onClick }) => {
  const isVideo = node.format?.startsWith("video");
  const roas = node.roas || 0;
  const tier =
    roas >= 4 ? "gold" :
    roas >= 3 ? "silver" :
    roas >= 2.5 ? "bronze" :
    null;

  const status = node.status || "active";
  const statusLabel = (STATUSES[status] || STATUSES.active).label;

  return (
    <button
      type="button"
      className={`web-grid-tile ${selected ? "is-selected" : ""} ${dimmed ? "is-dimmed" : ""}`}
      data-status={status}
      data-tier={tier || ""}
      onClick={onClick}
    >
      <div className="web-grid-tile__media">
        {node.thumbnail ? (
          <img src={node.thumbnail} alt={node.label} loading="lazy" />
        ) : (
          <div className="web-grid-tile__placeholder">
            {isVideo ? <Play size={28} /> : <ImageIcon size={28} />}
          </div>
        )}

        {node.scaling ? (
          <span className="web-grid-tile__pulse" title="Scaling">
            <Flame size={11} /> SCALING
          </span>
        ) : null}

        <span className={`web-grid-tile__roas web-grid-tile__roas--${tier || "none"}`}>
          {roas > 0 ? `${roas.toFixed(1)}×` : "—"}
        </span>
      </div>

      <div className="web-grid-tile__body">
        <div className="web-grid-tile__title">{node.label}</div>
        <div className="web-grid-tile__row">
          <span>${(node.spend || 0).toLocaleString()}</span>
          <span>·</span>
          <span>{(node.ctr || 0).toFixed(1)}% CTR</span>
          <span>·</span>
          <span>{Math.round((node.hookRate || 0) * 100)}% hook</span>
        </div>
        <div className="web-grid-tile__chips">
          <span className={`web-grid-tile__chip is-${status}`}>{statusLabel}</span>
          {node.funnel ? (
            <span className={`web-grid-tile__chip is-funnel-${node.funnel}`}>
              {node.funnel.toUpperCase()}
            </span>
          ) : null}
          {node.angle ? (
            <span className="web-grid-tile__chip">{node.angle}</span>
          ) : null}
        </div>
      </div>
    </button>
  );
};
